import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  Signal,
  signal,
  Type,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { NotificationPanelService } from '@core/services';
import { AuthService } from '@core/services/auth.service';
import { WINDOW } from '@core/window.service';
import { DropdownEvent, DropdownItem } from '@shared/abstraction';
import { contactUs } from '@shared/constants/contact-us';
import { Mapper } from '@shared/helpers/mapper';
import { DrawerSetting } from '@shared/models/drawer/drawer-setting';
import { CurrentUserService } from '@shared/services';
import { DrawerManagerService } from '@shared/services/drawer/drawer-manager.service';
import { filter, map, Observable, startWith } from 'rxjs';
import { NotificationPanelComponent } from 'src/app/modules/tax-result/components/notification-panel/notification-panel.component';
import { SettingsPanelComponent } from 'src/app/modules/tax-result/components/settings-panel/settings-panel.component';

@Component({
  standalone: false,
  selector: 'gstar-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  private readonly window = inject<Window>(WINDOW);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly drawerManagerService = inject(DrawerManagerService);
  private readonly notificationService = inject(NotificationPanelService);

  notificationCounter: WritableSignal<string> = signal('');

  private readonly currentUrl: Signal<string> = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  /** The dashboard page renders its own search bar, so the header one is hidden there. */
  showHeaderSearch: Signal<boolean> = computed(
    () => this.currentUrl().split('?')[0].replace(/\/+$/, '') !== '/dashboard'
  );

  isAdmin: Signal<boolean> = this.currentUserService.isAdmin;

  userMail: Signal<string> = computed(
    () => this.currentUserService.currentUserInfo()?.email ?? ''
  );
  userInitials: Signal<string> = this.currentUserService.userInitials;
  avatarMenuItems: DropdownItem[] = [
    { value: 'logout', label: 'Logout', iconName: 'lockclosed-locked-outline' },
  ];

  ngOnInit(): void {
    const totalNotificationCounter$: Observable<number> = this.isAdmin()
      ? this.notificationService.totalNotificationCounter$
      : this.notificationService.approvedSecuritiesCounter$;

    totalNotificationCounter$.subscribe((counter: number) =>
      this.notificationCounter.set(
        this.notificationService.getNotificationCountString(counter)
      )
    );
  }

  selectItem(event: DropdownEvent): void {
    if (event.selected.value === 'logout') {
      this.authService
        .logout()
        .subscribe(() => this.router.navigate(['/login']));
    }
  }

  openSettings(): void {
    const drawerSetting = Mapper.map({}, DrawerSetting, [
      {
        title: 'admin.settings.title',
        component: (): Type<SettingsPanelComponent> => SettingsPanelComponent,
        hideFooterButtons: true,
        mask: true,
      },
    ]);

    this.drawerManagerService.open(drawerSetting);
  }

  openNotifications(): void {
    const drawerSetting = Mapper.map({}, DrawerSetting, [
      {
        title: 'notification.title',
        component: (): Type<NotificationPanelComponent> =>
          NotificationPanelComponent,
        hideFooterButtons: true,
        mask: true,
      },
    ]);

    this.drawerManagerService.open(drawerSetting);
  }

  openContactUs(): void {
    const subject = encodeURIComponent(contactUs.subject);
    const body = encodeURIComponent(contactUs.body);

    const mailtoUrl = `mailto:${contactUs.mailTo}?subject=${subject}&body=${body}`;

    const width = Math.round(this.window.screen.width * 0.7);
    const height = Math.round(this.window.screen.height * 0.7);

    const left = Math.round((this.window.screen.width - width) / 2);
    const top = Math.round((this.window.screen.height - height) / 2);

    const windowFeatures = `width=${width},height=${height},left=${left},top=${top}`;

    this.window.open(mailtoUrl, '_blank', windowFeatures);
  }
}
